import apiClient from "@/api/client";
import type {
  AccessGroup,
  ContractorResourceAccess,
  DocumentVersion,
  Resource,
  ResourceAccess,
  ResourceCategory,
  ResourceDetail,
} from "../types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export interface AccessGroupInput {
  name: string;
  member_ids?: string[];
  contractor_ids?: string[];
}

export const repositoryApi = {
  // Categories
  listCategories: () =>
    apiClient.get<{ data: ResourceCategory[] }>("/repository/categories/").then(wrap),

  createCategory: (data: { name: string; description?: string; order?: number }) =>
    apiClient.post<{ data: ResourceCategory }>("/repository/categories/", data).then(wrap),

  updateCategory: (id: string, data: Partial<{ name: string; description: string; order: number }>) =>
    apiClient.patch<{ data: ResourceCategory }>(`/repository/categories/${id}/`, data).then(wrap),

  deleteCategory: (id: string) =>
    apiClient.delete(`/repository/categories/${id}/`),

  // Resources
  list: (params?: { category?: string; type?: string }) =>
    apiClient.get<{ data: Resource[] }>("/repository/", { params }).then(wrap),

  get: (id: string) =>
    apiClient.get<{ data: ResourceDetail }>(`/repository/${id}/`).then(wrap),

  create: (data: {
    title: string;
    description?: string;
    resource_type: string;
    category: string;
    url?: string;
  }) => apiClient.post<{ data: ResourceDetail }>("/repository/", data).then(wrap),

  update: (id: string, data: {
    title?: string;
    description?: string;
    category?: string;
    url?: string;
    manager_ids?: string[];
  }) => apiClient.patch<{ data: ResourceDetail }>(`/repository/${id}/`, data).then(wrap),

  softDelete: (id: string) =>
    apiClient.delete(`/repository/${id}/`),

  // Versions
  listVersions: (id: string) =>
    apiClient.get<{ data: DocumentVersion[] }>(`/repository/${id}/versions/`).then(wrap),

  uploadVersion: (id: string, file: File, changeComment?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (changeComment) form.append("change_comment", changeComment);
    return apiClient
      .post<{ data: DocumentVersion }>(`/repository/${id}/upload-version/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(wrap);
  },

  // Subscribe (bell)
  subscribe: (id: string) =>
    apiClient.post(`/repository/${id}/subscribe/`),

  unsubscribe: (id: string) =>
    apiClient.delete(`/repository/${id}/subscribe/`),

  // Access (director only)
  getAccess: (id: string) =>
    apiClient.get<{ data: ResourceAccess }>(`/repository/${id}/access/`).then(wrap),

  updateAccess: (
    id: string,
    data: { roles?: string[]; individual_users?: string[]; group_ids?: string[] },
  ) => apiClient.patch<{ data: ResourceAccess }>(`/repository/${id}/access/`, data).then(wrap),

  // Access groups (coordinators + directors; owner-private)
  listAccessGroups: () =>
    apiClient.get<{ data: AccessGroup[] }>("/repository/access-groups/").then(wrap),

  createAccessGroup: (data: AccessGroupInput) =>
    apiClient.post<{ data: AccessGroup }>("/repository/access-groups/", data).then(wrap),

  updateAccessGroup: (id: string, data: Partial<AccessGroupInput>) =>
    apiClient.patch<{ data: AccessGroup }>(`/repository/access-groups/${id}/`, data).then(wrap),

  deleteAccessGroup: (id: string) =>
    apiClient.delete(`/repository/access-groups/${id}/`),

  getContractorAccess: (id: string) =>
    apiClient.get<{ data: ContractorResourceAccess }>(`/repository/${id}/contractor-access/`).then(wrap),

  updateContractorAccess: (id: string, data: { contractors: string[] }) =>
    apiClient
      .patch<{ data: ContractorResourceAccess }>(`/repository/${id}/contractor-access/`, data)
      .then(wrap),
};
